<script setup lang="ts">
import { FlaskConical, History, Plug, UserCheck, UserX, Users } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { refDebounced } from "@vueuse/core";
import { computed, h, ref } from "vue";
import { toast } from "vue-sonner";
import { disableUser, enableUser, useAdminUsers } from "@/api/admin";
import type { AdminUser } from "@/api/contracts";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { confirm } from "@/composables/useConfirm";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import type { Column } from "@/lib/table";

definePage({ meta: { title: "Users", requires: { adminArea: "users" } } });

const queryClient = useQueryClient();
const { me, can } = useSession();
const q = useRouteQuery("q");
const debounced = refDebounced(q, 300);
const users = useAdminUsers(debounced);
const selected = ref<AdminUser | null>(null);
const open = ref(false);
const busy = ref(false);

const fullName = (u: AdminUser) => [u.firstName, u.lastName].filter(Boolean).join(" ") || u.username;

function show(u: AdminUser) {
  selected.value = u;
  open.value = true;
}

async function setEnabled(u: AdminUser, enabled: boolean) {
  const answer = enabled
    ? await confirm({ title: `Enable ${fullName(u)}?`, description: "They can sign in again with their previous roles." , confirmLabel: t.admin.enableUser })
    : await confirm({
        title: `Disable ${fullName(u)}?`,
        description: "Signs them out everywhere and blocks Smake and Keycloak sign-in. Their connections stop working.",
        confirmLabel: t.admin.disableUser,
        destructive: true,
        reason: true,
      });
  if (!answer) return;
  busy.value = true;
  try {
    const updated = enabled ? await enableUser(u.id) : await disableUser(u.id, answer.reason);
    selected.value = updated as AdminUser;
    toast.success(enabled ? "Enabled." : "Disabled.");
    await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
  } catch (err) {
    toast.error(errorMessage(err));
  } finally {
    busy.value = false;
  }
}

const columns = computed<Column<AdminUser>[]>(() => [
  {
    id: "name",
    header: "Name",
    accessorFn: (u) => fullName(u),
    meta: { mobile: "title" },
    cell: ({ row }) =>
      h("div", { class: "min-w-0 whitespace-normal" }, [
        h("p", { class: "font-medium [overflow-wrap:anywhere]" }, fullName(row.original)),
        h("p", { class: "text-xs font-normal text-muted-foreground [overflow-wrap:anywhere]" }, [row.original.username, row.original.email ? ` · ${row.original.email}` : ""]),
      ]),
  },
  {
    id: "groups",
    header: "Groups",
    accessorFn: (u) => u.groups.join(", "),
    meta: { mobile: "meta" },
    cell: ({ row }) => h("span", { class: "text-xs whitespace-normal text-muted-foreground" }, row.original.groups.join(", ") || "—"),
  },
  {
    id: "connections",
    header: "Connections",
    accessorFn: (u) => u.connections.length,
    meta: { mobile: "meta" },
    cell: ({ row }) => h("span", { class: "text-sm whitespace-normal" }, row.original.connections.join(", ") || "—"),
  },
  {
    id: "seen",
    header: "Last seen",
    accessorFn: (u) => u.lastSeenAt ?? "",
    meta: { mobile: "meta" },
    cell: ({ row }) => (row.original.lastSeenAt ? h(RelativeTime, { value: row.original.lastSeenAt, class: "text-sm whitespace-nowrap" }) : h("span", { class: "text-sm text-muted-foreground" }, "never")),
  },
  {
    id: "status",
    header: "Status",
    accessorFn: (u) => (u.enabled ? "enabled" : "disabled"),
    meta: { mobile: "badge" },
    cell: ({ row }) => (row.original.enabled ? null : h(StatusBadge, { status: "disabled" })),
  },
]);
</script>

<template>
  <div>
    <PageHeader :crumbs="[{ label: t.admin.title, to: '/admin' }]" :title="t.admin.users" :description="t.admin.usersDescription" />
    <FilterBar v-model:search="q" placeholder="Search by name, username or email…" label="Filter users" class="mb-5" />
    <ListSkeleton v-if="users.isPending.value" :rows="6" />
    <ErrorState v-else-if="users.error.value" :error="users.error.value" @retry="users.refetch()" />
    <EmptyState v-else-if="!users.data.value?.length" :icon="Users" :title="q ? t.common.noResults : 'No users found'" description="Users come from Keycloak." />
    <DataTable v-else :data="users.data.value" :columns="columns" row-clickable :row-key="(u) => u.id" caption="Users" @row-click="show" />

    <Sheet v-model:open="open">
      <SheetContent side="right" class="w-full overflow-y-auto sm:max-w-md">
        <template v-if="selected">
          <SheetHeader>
            <SheetTitle>{{ fullName(selected) }}</SheetTitle>
            <SheetDescription>{{ selected.username }}<template v-if="selected.email"> · {{ selected.email }}</template></SheetDescription>
          </SheetHeader>
          <div class="flex flex-col gap-5 px-4 pb-6">
            <div class="flex flex-wrap gap-2">
              <StatusBadge :status="selected.enabled ? 'connected' : 'disabled'" :label="selected.enabled ? 'Active' : t.common.disabled" />
              <span v-if="selected.lastSeenAt" class="text-sm text-muted-foreground">Last seen <RelativeTime :value="selected.lastSeenAt" /></span>
              <span v-else class="text-sm text-muted-foreground">Never used Smake</span>
            </div>
            <section>
              <p class="eyebrow mb-1.5">Keycloak groups</p>
              <div class="flex flex-wrap gap-1.5">
                <Badge v-for="g in selected.groups" :key="g" variant="accent">{{ g }}</Badge>
                <span v-if="!selected.groups.length" class="text-sm text-muted-foreground">none</span>
              </div>
            </section>
            <section>
              <p class="eyebrow mb-1.5">Connections</p>
              <div class="flex flex-wrap gap-1.5">
                <Badge v-for="c in selected.connections" :key="c" variant="muted"><Plug /> {{ c }}</Badge>
                <span v-if="!selected.connections.length" class="text-sm text-muted-foreground">none</span>
              </div>
            </section>
            <div class="flex flex-col gap-2">
              <Button as-child variant="outline"><RouterLink :to="{ path: '/admin/access/simulate', query: { user: selected.username } }"><FlaskConical /> Check what they can do</RouterLink></Button>
              <Button v-if="me?.canReadAllAudit && selected.principalId" as-child variant="outline">
                <RouterLink :to="{ path: '/audit', query: { user: selected.principalId } }"><History /> Their activity</RouterLink>
              </Button>
              <template v-if="can('admin.users') && selected.principalId !== me?.id">
                <Button v-if="selected.enabled" variant="destructive" :disabled="busy" @click="setEnabled(selected, false)"><UserX /> {{ t.admin.disableUser }}…</Button>
                <Button v-else :disabled="busy" @click="setEnabled(selected, true)"><UserCheck /> {{ t.admin.enableUser }}</Button>
              </template>
            </div>
          </div>
        </template>
      </SheetContent>
    </Sheet>
  </div>
</template>
